/**
 * Utility functions for role handling and email obfuscation
 */

/**
 * Get display name for a role
 * @param role The role value from the database (e.g., 'ic', 'owner')
 * @returns The display name (e.g., 'Viewer', 'Owner')
 */
export const getRoleDisplayName = (role: string | undefined | null): string => {
  if (!role) return 'N/A';
  const normalizedRole = role.toLowerCase();
  switch (normalizedRole) {
    case 'ic':
      return 'Viewer';
    case 'owner':
      return 'Owner';
    default:
      return role;
  }
};

/**
 * Obfuscate email domain for Viewer role users
 * Replaces the domain (but not TLD) with ***
 * e.g., "user@company.com" becomes "user@***.com"
 * e.g., "user@subdomain.company.co.uk" becomes "user@***.co.uk"
 *
 * @param email The email address to obfuscate
 * @returns The obfuscated email address
 */
export const obfuscateEmail = (email: string | undefined | null): string => {
  if (!email) return '';

  const atIndex = email.indexOf('@');
  if (atIndex === -1) return email;

  const localPart = email.substring(0, atIndex);
  const domainPart = email.substring(atIndex + 1);

  // Split domain into parts
  const domainParts = domainPart.split('.');

  if (domainParts.length < 2) {
    // Invalid domain format, return as-is
    return email;
  }

  // Keep the TLD (last part) or last two parts if it's a known two-part TLD
  const knownTwoPartTLDs = ['co.uk', 'com.au', 'co.nz', 'org.uk', 'net.au', 'com.br'];
  const lastTwoParts = domainParts.slice(-2).join('.');

  let tld: string;
  if (knownTwoPartTLDs.includes(lastTwoParts.toLowerCase())) {
    tld = lastTwoParts;
  } else {
    tld = domainParts[domainParts.length - 1];
  }

  return `${localPart}@***.${tld}`;
};
